<template>
    <form @submit.prevent="submit">
        <div class="p-6 mb-6 shadow-md rounded-md">
            <div class="text-2xl leading-6 flex justify-between items-center mb-4">
                <h3 class="font-bold">{{ stepTitle }}</h3>
                <inertia-link
                    :href="route('tenant.survey.preview', {survey: surveyModel.id})"
                    title="Preview Survey"
                    class="mr-auto ml-7 flex items-center font-bold text-lg leading-6"
                    aria-label="Preview Survey"
                >
                    <svg class="mr-2 w-6 h-6" fill="dark" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                        <path d="M505 442.7L405.3 343c-4.5-4.5-10.6-7-17-7H372c27.6-35.3 44-79.7 44-128C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c48.3 0 92.7-16.4 128-44v16.3c0 6.4 2.5 12.5 7 17l99.7 99.7c9.4 9.4 24.6 9.4 33.9 0l28.3-28.3c9.4-9.4 9.4-24.6.1-34zM208 336c-70.7 0-128-57.2-128-128 0-70.7 57.2-128 128-128 70.7 0 128 57.2 128 128 0 70.7-57.2 128-128 128z"/>
                    </svg>
                    Preview Survey
                </inertia-link>
                <p>Step {{ step }} of 5</p>
            </div>
            <div class="divide-y divide-dark">
                <div class="pb-5 flex items-start">
                    <div class="w-1/3">
                        <h4 class="text-lg leading-6 font-bold">Respondent slots</h4>
                        <p>Input how many users can fill in the survey before it ends</p>
                    </div>
                    <div class="w-1/2 px-3">
                        <jet-input type="number"
                                   min="0"
                                   v-model="form.paid_responses"
                                   @keyup="validatePaidResponses"
                                   @change="validatePaidResponses"
                                   @blur="validatePaidResponses"
                                   class="block"/>
                        <jet-input-error :message="form.errors.paid_responses"></jet-input-error>
                    </div>
                </div>
                <div v-if="surveyModel.status !== 0" class="py-5 flex items-start">
                    <div class="w-1/3">
                        <h4 class="text-lg leading-6 font-bold">Total survey respondent slots</h4>
                        <p>Total number of respondents that may fill in the survey before it ends</p>
                    </div>
                    <div class="w-1/2">
                        <p class="w-1/2 px-3 text-lg font-semibold">
                            {{ surveyModel.paid_responses + parseInt(form.paid_responses) }}
                        </p>
                    </div>
                </div>
                <div class="py-5 flex items-start">
                    <div class="w-1/3">
                        <h4 class="text-lg leading-6 font-bold">Prices per respondent slot</h4>
                    </div>
                    <div class="w-1/2">
                        <p v-for="(unitPrice, unitPriceIndex) in unitPrices"
                           :key="'unitPrice-' + unitPriceIndex"
                           class="w-1/2 px-3 text-lg font-semibold">
                            {{
                                unitPriceIndex === unitPrices.length - 1
                                    ? unitPrice.min_respondents + '+'
                                    : unitPrice.min_respondents + ' - '
                                    + (unitPrices[unitPriceIndex + 1].min_respondents - 1)
                            }}
                            : &pound;{{ (unitPrice.value / 100).toFixed(2) }}
                        </p>
                    </div>
                </div>
                <div class="py-5 flex items-start">
                    <div class="w-1/3">
                        <h4 class="text-lg leading-6 font-bold">Total Price</h4>
                    </div>
                    <p class="w-1/2 px-3 text-lg font-semibold">
                        &pound;{{ (totalPrice/100).toFixed(2) }}
                    </p>
                </div>
            </div>
        </div>

        <div class="flex justify-between">
            <jet-button v-if="surveyModel.status === 0"
                        @click="prevStep"
                        :disabled="form.processing"
                        :bg-dark="true"
            >
                <svg class="mr-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                </svg>
                Previous step
            </jet-button>
            <jet-button v-else
                        @click.prevent="back"
                        :disabled="form.processing"
                        :bg-dark="true"
            >
                <svg class="mr-2 h-3.5 w-2" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"/>
                </svg>
                Cancel
            </jet-button>
            <jet-button @click="nextStep"
                        :disabled="form.processing || form.hasErrors"
                        class="ml-auto"
                        :bg-dark="true"
            >
                {{ totalPrice ? 'Pay with Stripe' : 'Finish' }}
            </jet-button>
        </div>
    </form>
</template>

<script>
import JetButton from "@/Jetstream/Button";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import {useForm} from "@inertiajs/inertia-vue3";
import {computed} from "vue";

export default {
    name: "SurveyPriceCalculator",
    components: {JetButton, JetInput, JetInputError},
    emits: ['prev-step'],
    props: {
        surveyModel: {
            type: Object,
            required: true,
        },
        unitPrices: {
            type: Object,
            default: [{
                min_respondents: 0,
                value: 0,
            }],
        },
        order: {
            type: Object,
            required: false,
        },
        step: {
            type: Number,
            required: true
        },
        stepTitle: {
            type: String,
            required: true
        }
    },
    setup(props, {emit}) {
        const getInitialValueForNewRespondentSlots = () => {
            if (props.order) {
                return props.order.respondent_slots || 0;
            }

            if (props.surveyModel.status === 0) {
                return props.surveyModel.invited_respondents.length || 0;
            }

            return 0;
        }

        const form = useForm({
            step: 5,
            next_step: 0,
            paid_responses: getInitialValueForNewRespondentSlots(),
        });

        const nextStep = () => {
            form.next_step = 0;
        }

        const prevStep = () => {
            form.next_step = 4;
        }

        const submit = () => {
            form.transform(({step, next_step, paid_responses}) => {
                return {
                    step,
                    next_step,
                    paid_responses: paid_responses,
                    total_price: totalPrice.value,
                }
            });

            if (props.surveyModel.status === 0) {
                form.put(
                    route('tenant.survey.update', {tenant: props.surveyModel.tenant_id, survey: props.surveyModel.id}),
                    {
                        onSuccess: () => {
                            if (form.next_step === 4) {
                                emit('prev-step');
                            }
                        }
                    }
                );
            } else {
                form.post(
                    route('tenant.stripe.order', {
                        tenant: props.surveyModel.tenant_id,
                        survey: props.surveyModel.id
                    })
                );
            }

        }

        const priceIntervals = computed(() => {
            return props.unitPrices.map((element, index) => {
                if (index === props.unitPrices.length - 1) {
                    return {...element, max_respondent: null}
                }

                return {...element, max_respondent: props.unitPrices[index + 1].min_respondents - 1}
            });
        });

        const calculatePrice = (respondents, initialPriceDeductible) => {
            const calculation = priceIntervals.value.filter((unitPrice) => unitPrice.min_respondents <= respondents)
                .reduce(({totalUsers, totalPrice}, unitPrice) => {
                    if (!unitPrice.max_respondent || respondents <= unitPrice.max_respondent) {
                        totalPrice += unitPrice.value * (respondents - totalUsers);
                        totalUsers = respondents;
                    } else {
                        totalPrice += unitPrice.value * (unitPrice.max_respondent - totalUsers)
                        totalUsers = unitPrice.max_respondent
                    }
                    return {totalUsers, totalPrice};
                }, {totalUsers: 0, totalPrice: 0});
            return (calculation.totalPrice - initialPriceDeductible);
        }

        const initialPrice = calculatePrice(props.surveyModel.paid_responses, 0);

        const totalPrice = computed(() => {
            return calculatePrice(props.surveyModel.paid_responses + parseInt(form.paid_responses), initialPrice);
        });

        const validPaidResponses = computed(() => {
            //later we may need more complex logic based on prices here
            return form.paid_responses > 0;
        })

        const validatePaidResponses = () => {
            if (!validPaidResponses.value) {
                form.errors.paid_responses = "Invalid number of responses";
                form.hasErrors = true;
            } else {
                form.errors.paid_responses = null;
                form.hasErrors = false;
            }
        }

        const back = () => {
            window.history.back();
        }

        return {
            form,
            nextStep,
            prevStep,
            submit,
            priceIntervals,
            totalPrice,
            validatePaidResponses,
            back,
        }
    }
}
</script>

<style scoped>

</style>
